// frontend/src/pages/AdminPanel.js - Admin Dashboard Router (ENHANCED DEBUG)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SUPER_ADMIN_EMAIL } from '../config/appConfig';
import authService from '../services/authService';
import SubscriberManagement from '../components/admin/SubscriberManagement';
import UserManagement from '../components/admin/UserManagement';
import PasswordResetManagement from '../components/admin/PasswordResetManagement';
import SystemConfig from '../components/admin/SystemConfig';
import AdminStats from '../components/admin/AdminStats';
import PostArticle from '../components/admin/PostArticle';
import SocialMediaConfig from '../components/admin/SocialMediaConfig';
import AdminDashboard from './AdminDashboard'; // Regular user dashboard
import './AdminPanel.css';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('stats');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      console.log('🔍 [AdminPanel] Starting auth check...');

      // Check if token exists
      const token = authService.getToken();
      console.log('🔐 [AdminPanel] Token exists:', !!token);
      console.log('🔐 [AdminPanel] Token preview:', token ? token.substring(0, 20) + '...' : 'NO TOKEN');

      if (!token) {
        console.log('❌ [AdminPanel] No token found - user not logged in');
        navigate('/');
        return;
      }

      console.log('📞 [AdminPanel] Calling authService.getCurrentUser()...');
      const currentUser = await authService.getCurrentUser();
      console.log('✅ [AdminPanel] getCurrentUser() returned:', currentUser);
      console.log('📧 [AdminPanel] User email:', currentUser?.email);
      console.log('🔍 [AdminPanel] User object keys:', Object.keys(currentUser || {}));

      setUser(currentUser);

      // Check if user is super admin (configured via REACT_APP_ADMIN_EMAIL)
      const superAdminEmail = SUPER_ADMIN_EMAIL;
      const userEmail = currentUser?.email?.toLowerCase() || '';
      const isSuper = userEmail === superAdminEmail.toLowerCase();

      console.log('🔐 [AdminPanel] Super admin email:', superAdminEmail);
      console.log('🔐 [AdminPanel] User email (lowercase):', userEmail);
      console.log('🔐 [AdminPanel] Comparison result:', isSuper);
      console.log('🔐 [AdminPanel] Email match:', userEmail === superAdminEmail.toLowerCase());

      setIsSuperAdmin(isSuper);
      setLoading(false);

      if (isSuper) {
        console.log('✅✅✅ [AdminPanel] SUPER ADMIN ACCESS GRANTED! ✅✅✅');
        console.log('🎉 [AdminPanel] Should show admin panel with tabs...');
      } else {
        console.log('ℹ️ [AdminPanel] Regular user detected');
        console.log('📊 [AdminPanel] Will show basic AdminDashboard');
      }

    } catch (error) {
      console.error('❌ [AdminPanel] Auth check failed!');
      console.error('❌ [AdminPanel] Error:', error);
      console.error('❌ [AdminPanel] Error message:', error.message);
      console.error('❌ [AdminPanel] Error response:', error.response);
      navigate('/');
    }
  };

  console.log('🎨 [AdminPanel] Render - Loading:', loading, 'IsSuperAdmin:', isSuperAdmin);

  if (loading) {
    console.log('⏳ [AdminPanel] Rendering loading state...');
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Verifying admin access...</p>
      </div>
    );
  }

  // If not super admin, show regular dashboard
  if (!isSuperAdmin) {
    console.log('👤 [AdminPanel] Rendering AdminDashboard (regular user)');
    return <AdminDashboard />;
  }

  // Super Admin Panel
  console.log('🔐🔐🔐 [AdminPanel] RENDERING SUPER ADMIN PANEL! 🔐🔐🔐');
  return (
    <div className="admin-panel">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-title">
          <h1>🔐 Super Admin Panel</h1>
          <div className="admin-badge-container">
            <span className="admin-badge">SUPER ADMIN</span>
            <span className="admin-email">{user?.email}</span>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          📊 Dashboard
        </button>
        <button
          className={`tab ${activeTab === 'subscribers' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscribers')}
        >
          👥 Subscribers
        </button>
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          🎮 Users & Rewards
        </button>
        <button
          className={`tab ${activeTab === 'password-reset' ? 'active' : ''}`}
          onClick={() => setActiveTab('password-reset')}
        >
          🔑 Password Resets
        </button>
        <button
          className={`tab ${activeTab === 'post-article' ? 'active' : ''}`}
          onClick={() => setActiveTab('post-article')}
        >
          📌 Post Article
        </button>
        <button
          className={`tab ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          ⚙️ Configuration
        </button>
        <button
          className={`tab ${activeTab === 'social-media' ? 'active' : ''}`}
          onClick={() => setActiveTab('social-media')}
        >
          📣 Social Media
        </button>
      </div>

      {/* Tab Content */}
      <div className="admin-content">
        {activeTab === 'stats' && <AdminStats />}
        {activeTab === 'subscribers' && <SubscriberManagement />}
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'password-reset' && <PasswordResetManagement />}
        {activeTab === 'post-article' && <PostArticle />}
        {activeTab === 'config' && <SystemConfig />}
        {activeTab === 'social-media' && <SocialMediaConfig />}
      </div>
    </div>
  );
};

export default AdminPanel;